import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import Router, { useRouter } from 'next/router';
import { ThemeProvider } from 'styled-components';
import NextNprogress from 'nextjs-progressbar';
import Cookies from 'js-cookie';
import { ToastContainer } from 'react-toastify';

import { GlobalStyle } from 'themes/global';
import { useCart } from 'hooks/useCart';
import { useWindowSize } from 'hooks/useWindowSize';
import Loading from 'components/loading';
import Tag from 'componentsV2/Tag';
import {
  loadingUpsellUrls,
  urlsWithBackButton,
  urlsWithoutLogo,
  urlsWithoutSellerBox,
  urlsWTP,
} from 'utils/globalFunctions';
import { initTagManager } from 'utils/tagManagerInit';
import BackButton from 'componentsV2/BackButton';
import SellerBox from 'components/sellerBox';
import dynamic from 'next/dynamic';
import useSessionStorage from 'hooks/useSessionStorage';
import setContextDataLead from 'utils/setContextDataLead';
import { useProductFlow } from 'hooks/useProductFlow';
import { useRouterQuery } from 'hooks/useRouterQuery';
import { OFFER_COUNTDOWN_BAR_SLOT_ID } from 'modules/offer-availability';

const Provider = dynamic(() => import('componentsV2/DS/Provider'), {
  ssr: false,
});

const MERCURY_DEFAULT_LOGO =
  'https://storage.googleapis.com/checkout-front-public/Mercury/logo-mercury.svg';
const MERCURY_DEFAULT_FAVICON =
  'https://storage.googleapis.com/checkout-front-public/Mercury/favicon-mercury.svg';

export default function AppRoot({ children, productGTM, productTitle }) {
  const router = useRouter();
  const routerQuery = useRouterQuery();
  const context = useCart();
  const { currentStep } = useProductFlow();

  const {
    productTheme,
    loading,
    sellerName,
    productFlow,
    dsBrand,
    dsMode,
    legacyProductTheme,
  } = context;

  const { width } = useWindowSize();
  const [hasHomologCookie, setHasHomologCookie] = useState(false);
  const [title, setTitle] = useState(productTitle || '');
  const [dataForm] = useSessionStorage('dataForm', null);
  const [dataLead] = useSessionStorage('dataLead', null);

  useEffect(() => {
    if (productGTM) {
      initTagManager({
        googleTagManagerId: productGTM,
      });
    }

    setHasHomologCookie(Cookies.get('homologacao-cookie'));
  }, [productGTM]);

  useEffect(() => {
    if (dataForm && dataLead) {
      setContextDataLead(dataLead, dataForm, context);
    }
  }, [dataForm, dataLead, context]);

  useEffect(() => {
    if (productTitle) {
      setTitle(productTitle);
    }
  }, [productTitle]);

  const logoByProductFlow = productFlow?.props?.media?.logo;
  const logoByProductFlowStep = currentStep?.props?.media?.logo;
  const logoWidthByProductFlow = productFlow?.props?.style?.logo?.width;
  const faviconByProductFlow = productFlow?.props?.media?.favicon;

  const showBackButton = urlsWithBackButton?.includes(Router?.route);
  const showLogo =
    !urlsWithoutLogo?.includes(Router?.route) || !!logoByProductFlowStep;
  const isWTP = urlsWTP(Router?.route);
  const hideSellerBox = urlsWithoutSellerBox?.includes(Router?.route);

  const localLogo = legacyProductTheme?.style?.assets?.logo?.file
    ? `/static/logo/${legacyProductTheme.style?.assets.logo.file}`
    : null;

  const localFavicon = legacyProductTheme?.style?.assets?.favicon
    ? `/static/favicon/${legacyProductTheme.style?.assets.favicon}`
    : null;

  const logoSource =
    logoByProductFlowStep ||
    logoByProductFlow ||
    localLogo ||
    MERCURY_DEFAULT_LOGO;

  const mobileLogoWidth = logoWidthByProductFlow || '110px';

  const logoWidth = logoWidthByProductFlow;

  const favicon =
    faviconByProductFlow || localFavicon || MERCURY_DEFAULT_FAVICON;

  return (
    // @TODO: Verificar como podemos remover a dependencia do theme
    <div className="container-wrapper" lang="pt-BR">
      <Head>
        <title>Checkout - {title}</title>
        <link rel="icon" href={favicon} />
      </Head>

      {/* @TODO: Verificar como podemos remover a dependencia do theme */}
      <NextNprogress
        options={{ showSpinner: false }}
        color={productTheme?.color?.secondary?.pure}
        startPosition={0.3}
        stopDelayMs={200}
        height={2}
      />

      {/* @TODO: Verificar como podemos remover a dependencia do theme */}
      <ThemeProvider theme={productTheme}>
        <GlobalStyle whiteColor />

        <Provider brand={dsBrand || 'ms'} mode={dsMode || 'dark'}>
          {!!sellerName &&
            routerQuery.seller !== 'performance' &&
            !hideSellerBox && (
              <SellerBox
                dataCy="@attendance-tag/name-seller"
                isWTP={isWTP || dsBrand === 'live'}
              />
            )}

          {/* FIN-8011: faixa do contador da oferta (portal), abaixo do "Atendimento".
              Fica fixa no topo ao rolar a página (sticky), com o fundo da página
              por baixo (o mesmo do body) e abaixo do loading (z-15), dos modais e da
              Tag de homologação (z-14, depois no DOM). */}
          <div
            id={OFFER_COUNTDOWN_BAR_SLOT_ID}
            className="sticky top-0 z-[14]"
            style={{ backgroundColor: productTheme?.color?.primary?.pure }}
          />

          {!!hasHomologCookie && (
            <Tag
              hasSellerBox={
                !!sellerName && routerQuery.seller !== 'performance'
              }
              label="Homologação"
              onClick={() => {
                try {
                  Cookies.remove('homologacao-cookie');
                  window.location.href = router.asPath.replace(
                    '&homologacao=on',
                    ''
                  );
                } catch (error) {
                  window.location.href = '/lead';
                }
              }}
              bg={isWTP ? '#055EA6' : null}
            />
          )}

          {showLogo && (
            <div className={`logo ${showBackButton ? 'logo-back-button' : ''}`}>
              {showBackButton && (
                <BackButton
                  onClick={() => {
                    router.back();
                  }}
                  style={{ marginTop: 80 }}
                >
                  Voltar
                </BackButton>
              )}
              <img
                src={logoSource}
                style={{
                  width:
                    width < 250
                      ? '110px'
                      : width < 375
                        ? mobileLogoWidth
                        : logoWidth,
                }}
                alt="Logo"
              />
            </div>
          )}
          {loading && (
            <Loading
              isUpsell={
                loadingUpsellUrls.includes(Router.route) || dsBrand === 'live'
              }
            />
          )}
          <div className="max-w-[505px] px-5 mx-auto">{children}</div>
        </Provider>
      </ThemeProvider>
      <ToastContainer />
    </div>
  );
}
